import React, { useState, useEffect, useCallback } from 'react';
import { format, isToday, isYesterday } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { usersAPI } from '../utils/api';
import Avatar from './Avatar';
import CreateGroupModal from './CreateGroupModal';
import BroadcastModal from './BroadcastModal';
import toast from 'react-hot-toast';

export default function Sidebar({
  conversations, loadingConvs, activeConversationId,
  onSelectConversation, onOpenNewChat, onOpenProfile,
  onOpenChangePassword, totalUnread, isConnected, onGroupCreated
}) {
  const { user, logout } = useAuth();
  const { isUserOnline } = useSocket();
  const [tab, setTab] = useState('chats');
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingEmp, setLoadingEmp] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);

  const loadEmployees = useCallback(async () => {
    setLoadingEmp(true);
    try {
      const res = await usersAPI.getAll({ search });
      // Fix 5: client-side double-check to exclude self
      const myId = user?._id;
      const filtered = res.data.data.filter(e => String(e._id) !== String(myId));
      setEmployees(filtered);
    } catch { toast.error('Failed to load employees.'); }
    finally { setLoadingEmp(false); }
  }, [search, user?._id]);

  useEffect(() => { if (tab === 'employees') loadEmployees(); }, [tab, loadEmployees]);

  const filteredConvs = conversations.filter(c => {
    const name = c.isGroup ? c.groupName : c.participant?.name;
    return name?.toLowerCase().includes(search.toLowerCase());
  });

  const fmtTime = d => {
    if (!d) return '';
    const dt = new Date(d);
    if (isToday(dt)) return format(dt, 'HH:mm');
    if (isYesterday(dt)) return 'Yesterday';
    return format(dt, 'dd/MM');
  };

  const onlineCount = employees.filter(e => isUserOnline(e._id)).length;

  const GroupAvatar = ({ name }) => (
    <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--brand)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
      {name?.charAt(0)?.toUpperCase() || 'G'}
    </div>
  );

  return (
    <>
      <div className="sidebar">
        {/* Top Header */}
        <div className="sidebar-top">
          <div className="sidebar-workspace">
            <div className="sidebar-ws-icon">💬</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="sidebar-ws-name">TeamChat</div>
              <div className="sidebar-ws-status" style={{ color: isConnected ? 'var(--online)' : 'var(--warning)' }}>
                {isConnected ? '● Connected' : '○ Reconnecting…'}
              </div>
            </div>
            {totalUnread > 0 && <span className="unread-badge">{totalUnread}</span>}
          </div>

          {/* Search */}
          <div className="sidebar-search" style={{ marginBottom: 8 }}>
            <span className="sidebar-search-icon">🔍</span>
            <input
              placeholder={tab === 'chats' ? 'Search conversations…' : 'Search people…'}
              value={search} onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* Fix 3: Tabs — constrained so they never overflow */}
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              className={`sidebar-tab${tab === 'chats' ? ' active' : ''}`}
              onClick={() => setTab('chats')}
              style={{ flex: 1 }}
            >
              💬 Chats{totalUnread > 0 ? ` (${totalUnread})` : ''}
            </button>
            <button
              className={`sidebar-tab${tab === 'employees' ? ' active' : ''}`}
              onClick={() => setTab('employees')}
              style={{ flex: 1 }}
            >
              👥 People{tab === 'employees' && onlineCount > 0 ? ` (${onlineCount})` : ''}
            </button>
          </div>
        </div>

        {/* List */}
        <div className="sidebar-list">
          {tab === 'chats' ? (
            <>
              {/* Action buttons — Fix 3: full-width stack, never overflow */}
              <div style={{ padding: '8px 10px 4px', display: 'flex', gap: 6 }}>
                <button
                  onClick={() => setShowCreateGroup(true)}
                  style={{ flex: 1, height: 28, background: 'rgba(88,101,242,.15)', border: '1px solid rgba(88,101,242,.25)', borderRadius: 6, color: '#a8b4ff', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  + Group
                </button>
                <button
                  onClick={() => setShowBroadcast(true)}
                  style={{ flex: 1, height: 28, background: 'rgba(245,158,11,.12)', border: '1px solid rgba(245,158,11,.25)', borderRadius: 6, color: '#fbbf24', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  📢 Broadcast
                </button>
              </div>

              {filteredConvs.length === 0 ? (
                <div style={{ padding: '20px 16px', textAlign: 'center', color: 'var(--sidebar-text)', fontSize: 12 }}>
                  {search ? 'No conversations match.' : 'No conversations yet.\nClick People tab to start chatting.'}
                </div>
              ) : filteredConvs.map(conv => (
                <div key={conv._id}
                  className={`sidebar-item${activeConversationId === conv._id ? ' active' : ''}`}
                  onClick={() => onSelectConversation(conv)}>
                  {conv.isGroup
                    ? <GroupAvatar name={conv.groupName} />
                    : <Avatar user={conv.participant} size="sm" showStatus isOnline={isUserOnline(conv.participant?._id)} />
                  }
                  <div className="sidebar-item-info">
                    <div className="sidebar-item-name">
                      {conv.isGroup ? conv.groupName : conv.participant?.name}
                      {conv.isGroup && (
                        <span style={{ fontSize: 9, background: 'rgba(88,101,242,.3)', color: '#a8b4ff', borderRadius: 3, padding: '1px 4px', marginLeft: 5 }}>GRP</span>
                      )}
                    </div>
                    <div className="sidebar-item-preview">
                      {conv.lastMessageContent || 'Tap to start chatting'}
                    </div>
                  </div>
                  <div className="sidebar-item-meta">
                    {conv.lastMessageAt && <span className="sidebar-item-time">{fmtTime(conv.lastMessageAt)}</span>}
                    {conv.unreadCount > 0 && <span className="unread-badge">{conv.unreadCount}</span>}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              {/* People Tab */}
              <div className="sidebar-section">
                <span>Employees ({employees.length})</span>
                {onlineCount > 0 && <span style={{ color: 'var(--online)', fontWeight: 700 }}>{onlineCount} online</span>}
              </div>

              {loadingEmp ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--sidebar-text)', fontSize: 12 }}>Loading…</div>
              ) : employees.length === 0 ? (
                <div style={{ padding: '20px 16px', textAlign: 'center', color: 'var(--sidebar-text)', fontSize: 12 }}>
                  No other employees found.<br />
                  <span style={{ fontSize: 11, opacity: .6 }}>Employees appear here once admin approves them.</span>
                </div>
              ) : employees.map(emp => (
                <div key={emp._id} className="sidebar-item"
                  onClick={() => { onOpenNewChat(emp._id); setTab('chats'); }}>
                  <Avatar user={emp} size="sm" showStatus isOnline={isUserOnline(emp._id)} />
                  <div className="sidebar-item-info">
                    <div className="sidebar-item-name">{emp.name}</div>
                    <div className="sidebar-item-preview">
                      {emp.designation || emp.employeeId}
                    </div>
                  </div>
                  <span style={{ fontSize: 11, color: isUserOnline(emp._id) ? 'var(--online)' : 'var(--offline)', fontWeight: 600, flexShrink: 0 }}>
                    ●{isUserOnline(emp._id) ? ' On' : ' Off'}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Footer — Fix 1: Change Password button visible here */}
        <div className="sidebar-footer">
          <div className="sidebar-user" onClick={onOpenProfile} title="Edit Profile">
            <Avatar user={user} size="sm" showStatus isOnline />
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user?.name}</div>
              <div className="sidebar-user-dept">{user?.designation || 'Employee'}</div>
            </div>
          </div>
          {/* Action buttons row */}
          <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
            <button className="sidebar-user-btn" style={{ flex: 1, fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,.6)' }}
              onClick={onOpenProfile} title="Edit Profile">
              ⚙ Profile
            </button>
            <button className="sidebar-user-btn" style={{ flex: 1, fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,.6)' }}
              onClick={onOpenChangePassword} title="Change Password">
              🔒 Password
            </button>
            <button className="sidebar-user-btn" style={{ flex: 1, fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,.6)' }}
              onClick={logout} title="Sign Out">
              ↩ Logout
            </button>
          </div>
        </div>
      </div>

      {showCreateGroup && (
        <CreateGroupModal
          onClose={() => setShowCreateGroup(false)}
          onGroupCreated={group => { onGroupCreated(group); setTab('chats'); }}
        />
      )}
      {showBroadcast && <BroadcastModal onClose={() => setShowBroadcast(false)} />}
    </>
  );
}
